import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Card } from 'primereact/card';
import { Dialog } from 'primereact/dialog';
import { Password } from 'primereact/password';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import './DeveloperGuidePage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function DeveloperGuidePage() {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [systemOverview, setSystemOverview] = useState(null);
    
    // Password change states
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [changingPassword, setChangingPassword] = useState(false);

    useEffect(() => {
        verifyAccess();
    }, []);

    const verifyAccess = async () => {
        const token = localStorage.getItem('developerToken');
        if (!token) {
            navigate('/admin');
            return;
        }

        try {
            const response = await fetch(`${API_BASE_URL}/developer/verify`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            
            if (data.success) {
                setUser(data.user);
                fetchSystemOverview(token);
            } else {
                localStorage.removeItem('developerToken');
                localStorage.removeItem('developerUser');
                navigate('/admin');
            }
        } catch (err) {
            navigate('/admin');
        } finally {
            setLoading(false);
        }
    };

    const fetchSystemOverview = async (token) => {
        try {
            const response = await fetch(`${API_BASE_URL}/developer/system-overview`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setSystemOverview(data.overview);
            }
        } catch (err) {
            console.error('Failed to fetch system overview:', err);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('developerToken');
        localStorage.removeItem('developerUser');
        navigate('/admin');
    };

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordError('All fields are required');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('New passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            setPasswordError('Password must be at least 8 characters');
            return;
        }

        setChangingPassword(true);

        try {
            const token = localStorage.getItem('developerToken');
            const response = await fetch(`${API_BASE_URL}/developer/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ currentPassword, newPassword })
            });

            const data = await response.json();
            if (data.success) {
                setPasswordSuccess('Password changed successfully!');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setTimeout(() => setShowPasswordDialog(false), 2000);
            } else {
                setPasswordError(data.message || 'Failed to change password');
            }
        } catch (err) {
            setPasswordError('Failed to change password');
        } finally {
            setChangingPassword(false);
        }
    };

    if (loading) {
        return (
            <div className="dev-loading">
                <ProgressSpinner />
                <p>Verifying access...</p>
            </div>
        );
    }

    return (
        <div className="developer-guide-page">
            {/* Header */}
            <header className="dev-header">
                <div className="dev-header-left">
                    <i className="pi pi-code"></i>
                    <div>
                        <h1>Developer Guide</h1>
                        <span>NEDP Education System Documentation</span>
                    </div>
                </div>
                <div className="dev-header-right">
                    <Button
                        icon="pi pi-arrow-left"
                        label="Back to App"
                        className="p-button-secondary p-button-sm"
                        onClick={() => navigate("/admin")}
                    />
                    <span className="dev-user">
                        <i className="pi pi-user"></i>
                        {user?.name}
                    </span>
                    <Button
                        icon="pi pi-lock"
                        label="Change Password"
                        className="p-button-outlined p-button-sm"
                        onClick={() => {
                            setShowPasswordDialog(true);
                            setPasswordError('');
                            setPasswordSuccess('');
                            setCurrentPassword('');
                            setNewPassword('');
                            setConfirmPassword('');
                        }}
                    />
                    <Button
                        icon="pi pi-sign-out"
                        label="Logout"
                        className="p-button-danger p-button-sm"
                        onClick={handleLogout}
                    />
                </div>
            </header>

            {/* System Overview Cards */}
            {systemOverview && (
                <div className="dev-overview">
                    <Card className="overview-card">
                        <div className="overview-icon schools">
                            <i className="pi pi-building"></i>
                        </div>
                        <div className="overview-info">
                            <span className="overview-value">{systemOverview.schools}</span>
                            <span className="overview-label">Schools</span>
                        </div>
                    </Card>
                    <Card className="overview-card">
                        <div className="overview-icon teachers">
                            <i className="pi pi-users"></i>
                        </div>
                        <div className="overview-info">
                            <span className="overview-value">{systemOverview.teachers}</span>
                            <span className="overview-label">Teachers</span>
                        </div>
                    </Card>
                    <Card className="overview-card">
                        <div className="overview-icon students">
                            <i className="pi pi-id-card"></i>
                        </div>
                        <div className="overview-info">
                            <span className="overview-value">{systemOverview.students}</span>
                            <span className="overview-label">Students</span>
                        </div>
                    </Card>
                    <Card className="overview-card">
                        <div className="overview-icon users">
                            <i className="pi pi-user"></i>
                        </div>
                        <div className="overview-info">
                            <span className="overview-value">{systemOverview.users}</span>
                            <span className="overview-label">Active Users</span>
                        </div>
                    </Card>
                </div>
            )}

            {/* Main Content */}
            <div className="dev-content">
                <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
                    <TabPanel header="Architecture" leftIcon="pi pi-sitemap mr-2">
                        <div className="doc-section">
                            <h2>System Architecture</h2>
                            <div className="architecture-diagram">
                                <pre>{`
┌─────────────────────────────────────────────────────────────────────────────┐
│                              DEVELOPER WORKFLOW                              │
└─────────────────────────────────────────────────────────────────────────────┘

    ┌──────────────┐
    │  Developer   │
    │   Machine    │
    └──────┬───────┘
           │ git push origin main
           ▼
    ┌──────────────┐
    │    GitHub    │
    │  Repository  │
    └──────┬───────┘
           │ HTTP POST (webhook)
           ▼
┌─────────────────────────────────────────────────────────────────────────────┐
│                           PRODUCTION SERVER                                  │
│                                                                              │
│  ┌─────────────────────────────────────────────────────────────────────┐   │
│  │                            NGINX (443)                               │   │
│  │              Reverse Proxy + SSL Termination                         │   │
│  └─────────────────────────────────────────────────────────────────────┘   │
│              │                    │                    │                    │
│              ▼                    ▼                    ▼                    │
│  ┌───────────────┐   ┌───────────────────┐   ┌─────────────────┐          │
│  │ React App     │   │ Express API       │   │ Webhook Server  │          │
│  │ (Static)      │   │ (Port 3001)       │   │ (Port 9000)     │          │
│  └───────────────┘   └─────────┬─────────┘   └────────┬────────┘          │
│                                │                      │                    │
│                                ▼                      ▼                    │
│                    ┌───────────────────┐   ┌─────────────────┐            │
│                    │     MySQL DB      │   │   deploy.sh     │            │
│                    │   (education_db)  │   │  (Auto Deploy)  │            │
│                    └───────────────────┘   └─────────────────┘            │
│                                                                              │
└─────────────────────────────────────────────────────────────────────────────┘
                `}</pre>
                            </div>

                            <h3>Technology Stack</h3>
                            <div className="tech-stack">
                                <div className="tech-item">
                                    <span className="tech-name">Frontend</span>
                                    <span className="tech-desc">React 18, PrimeReact, CSS3</span>
                                </div>
                                <div className="tech-item">
                                    <span className="tech-name">Backend</span>
                                    <span className="tech-desc">Node.js, Express.js</span>
                                </div>
                                <div className="tech-item">
                                    <span className="tech-name">Database</span>
                                    <span className="tech-desc">MySQL 8.0</span>
                                </div>
                                <div className="tech-item">
                                    <span className="tech-name">Web Server</span>
                                    <span className="tech-desc">Nginx with SSL</span>
                                </div>
                                <div className="tech-item">
                                    <span className="tech-name">CI/CD</span>
                                    <span className="tech-desc">GitHub Webhooks</span>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="CI/CD Pipeline" leftIcon="pi pi-sync mr-2">
                        <div className="doc-section">
                            <h2>Continuous Integration / Continuous Deployment</h2>
                            
                            <h3>How It Works</h3>
                            <div className="pipeline-steps">
                                <div className="pipeline-step">
                                    <div className="step-number">1</div>
                                    <div className="step-content">
                                        <h4>Push Code</h4>
                                        <p>Developer pushes code to GitHub (main branch)</p>
                                        <code>git push origin main</code>
                                    </div>
                                </div>
                                <div className="pipeline-step">
                                    <div className="step-number">2</div>
                                    <div className="step-content">
                                        <h4>Webhook Triggered</h4>
                                        <p>GitHub sends POST to server webhook endpoint</p>
                                        <code>POST https://staging.jihltd.com/webhook</code>
                                    </div>
                                </div>
                                <div className="pipeline-step">
                                    <div className="step-number">3</div>
                                    <div className="step-content">
                                        <h4>Signature Verified</h4>
                                        <p>Server verifies HMAC-SHA256 signature</p>
                                    </div>
                                </div>
                                <div className="pipeline-step">
                                    <div className="step-number">4</div>
                                    <div className="step-content">
                                        <h4>Deploy Script Runs</h4>
                                        <p>Automated build and restart</p>
                                        <code>./deploy.sh</code>
                                    </div>
                                </div>
                                <div className="pipeline-step">
                                    <div className="step-number">5</div>
                                    <div className="step-content">
                                        <h4>Site Updated</h4>
                                        <p>Changes live on production</p>
                                    </div>
                                </div>
                            </div>

                            <h3>Key Files</h3>
                            <div className="file-list">
                                <div className="file-item">
                                    <i className="pi pi-file"></i>
                                    <span className="file-path">/srv/teacher-attendance-app/deploy.sh</span>
                                    <span className="file-desc">Main deployment script</span>
                                </div>
                                <div className="file-item">
                                    <i className="pi pi-file"></i>
                                    <span className="file-path">/srv/teacher-attendance-app/webhook/webhook-server.js</span>
                                    <span className="file-desc">Webhook listener service</span>
                                </div>
                                <div className="file-item">
                                    <i className="pi pi-file"></i>
                                    <span className="file-path">/etc/systemd/system/nedp-webhook.service</span>
                                    <span className="file-desc">Systemd service file</span>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="API Reference" leftIcon="pi pi-server mr-2">
                        <div className="doc-section">
                            <h2>API Endpoints</h2>
                            
                            <div className="api-group">
                                <h3>Authentication</h3>
                                <div className="api-endpoint">
                                    <span className="method post">POST</span>
                                    <span className="path">/api/admin/login</span>
                                    <span className="desc">Admin login</span>
                                </div>
                                <div className="api-endpoint">
                                    <span className="method post">POST</span>
                                    <span className="path">/api/developer/login</span>
                                    <span className="desc">Developer login (cross-school access)</span>
                                </div>
                            </div>

                            <div className="api-group">
                                <h3>Attendance</h3>
                                <div className="api-endpoint">
                                    <span className="method get">GET</span>
                                    <span className="path">/api/attendance/daily</span>
                                    <span className="desc">Get daily attendance</span>
                                </div>
                                <div className="api-endpoint">
                                    <span className="method get">GET</span>
                                    <span className="path">/api/attendance/monthly</span>
                                    <span className="desc">Get monthly attendance</span>
                                </div>
                                <div className="api-endpoint">
                                    <span className="method post">POST</span>
                                    <span className="path">/api/attendance/manual-entry</span>
                                    <span className="desc">Create manual attendance entry</span>
                                </div>
                                <div className="api-endpoint">
                                    <span className="method post">POST</span>
                                    <span className="path">/api/attendance/verify</span>
                                    <span className="desc">Verify/reject manual entry</span>
                                </div>
                            </div>

                            <div className="api-group">
                                <h3>Settings</h3>
                                <div className="api-endpoint">
                                    <span className="method get">GET</span>
                                    <span className="path">/api/settings/attendance-roles</span>
                                    <span className="desc">Get role assignments</span>
                                </div>
                                <div className="api-endpoint">
                                    <span className="method post">POST</span>
                                    <span className="path">/api/settings/attendance-roles</span>
                                    <span className="desc">Save role assignments</span>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="Database" leftIcon="pi pi-database mr-2">
                        <div className="doc-section">
                            <h2>Database Schema</h2>
                            
                            <h3>Key Tables</h3>
                            <div className="table-list">
                                <div className="table-item">
                                    <h4>teacher_attendance</h4>
                                    <p>Stores daily attendance records</p>
                                    <code>id, teacher_id, attendance_date, time_in, time_out, status, is_manual_entry, is_verified</code>
                                </div>
                                <div className="table-item">
                                    <h4>attendance_role_assignment</h4>
                                    <p>Role assignments for data entry and verification</p>
                                    <code>id, role_type, user_login_id, assigned_by, assigned_at</code>
                                </div>
                                <div className="table-item">
                                    <h4>developer_access</h4>
                                    <p>Developer access control (cross-school)</p>
                                    <code>id, user_login_id, is_active, created_date</code>
                                </div>
                                <div className="table-item">
                                    <h4>data_changed_log_manager</h4>
                                    <p>Audit trail for all changes</p>
                                    <code>id, date, attribute_name, old_data, new_data, reference, comment, table_manager_id, user_login_id</code>
                                </div>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header="Commands" leftIcon="pi pi-terminal mr-2">
                        <div className="doc-section">
                            <h2>Useful Commands</h2>
                            
                            <h3>Service Management</h3>
                            <div className="command-list">
                                <div className="command-item">
                                    <code>systemctl status attendance-api</code>
                                    <span>Check API service status</span>
                                </div>
                                <div className="command-item">
                                    <code>systemctl restart attendance-api</code>
                                    <span>Restart API service</span>
                                </div>
                                <div className="command-item">
                                    <code>systemctl status nedp-webhook</code>
                                    <span>Check webhook service</span>
                                </div>
                                <div className="command-item">
                                    <code>nginx -s reload</code>
                                    <span>Reload Nginx config</span>
                                </div>
                            </div>

                            <h3>Logs</h3>
                            <div className="command-list">
                                <div className="command-item">
                                    <code>tail -f /var/log/nedp-webhook.log</code>
                                    <span>Watch webhook logs</span>
                                </div>
                                <div className="command-item">
                                    <code>journalctl -u attendance-api -f</code>
                                    <span>Watch API logs</span>
                                </div>
                                <div className="command-item">
                                    <code>tail -f /var/log/nginx/nedp-staging-ssl-error.log</code>
                                    <span>Watch Nginx errors</span>
                                </div>
                            </div>

                            <h3>Deployment</h3>
                            <div className="command-list">
                                <div className="command-item">
                                    <code>cd /srv/teacher-attendance-app && ./deploy.sh</code>
                                    <span>Manual deployment</span>
                                </div>
                                <div className="command-item">
                                    <code>cd /srv/teacher-attendance-app/frontend && npm run build</code>
                                    <span>Build frontend only</span>
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                </TabView>
            </div>

            {/* Password Change Dialog */}
            <Dialog
                visible={showPasswordDialog}
                onHide={() => setShowPasswordDialog(false)}
                header="Change Password"
                style={{ width: '400px' }}
                modal
            >
                <div className="password-dialog-content">
                    {passwordError && (
                        <Message severity="error" text={passwordError} style={{ width: '100%', marginBottom: '16px' }} />
                    )}
                    {passwordSuccess && (
                        <Message severity="success" text={passwordSuccess} style={{ width: '100%', marginBottom: '16px' }} />
                    )}

                    <div className="field">
                        <label>Current Password</label>
                        <Password
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            feedback={false}
                            toggleMask
                            style={{ width: '100%' }}
                            inputStyle={{ width: '100%' }}
                        />
                    </div>

                    <div className="field">
                        <label>New Password</label>
                        <Password
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            toggleMask
                            style={{ width: '100%' }}
                            inputStyle={{ width: '100%' }}
                        />
                    </div>

                    <div className="field">
                        <label>Confirm New Password</label>
                        <Password
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            feedback={false}
                            toggleMask
                            style={{ width: '100%' }}
                            inputStyle={{ width: '100%' }}
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                        <Button
                            label="Cancel"
                            className="p-button-text"
                            onClick={() => setShowPasswordDialog(false)}
                        />
                        <Button
                            label="Change Password"
                            icon="pi pi-check"
                            onClick={handleChangePassword}
                            loading={changingPassword}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default DeveloperGuidePage;
